/**
 * Comments model — explicit state definition for S011 Comments screen.
 *
 * Figma authority: R4VYIdXEv7HYIXtpDMEvHY, node 38:2510
 *
 * Elm-ish architecture: state is explicit and typed.
 * No implicit state machines encoded in useState hooks.
 *
 * Security posture:
 * - No account_id, legal name, phone, email, address in this model
 * - Actor identity is server-derived from authenticated session
 * - Comment content is never logged or included in error messages
 * - Anonymous authors do not expose age or identity beyond display name
 */

// =============================================================================
// Types
// =============================================================================

/**
 * Author summary for display in comments.
 * Represents either a named user or anonymous author.
 */
export interface AuthorSummary {
  /** Display name from profile or "Аноним" for anonymous */
  readonly displayName: string;
  /** Age from profile.age (null for anonymous or when not set) */
  readonly age: number | null;
  /** First letter of displayName, uppercase, for avatar */
  readonly initials: string;
  /** Whether this is an anonymous author */
  readonly isAnonymous: boolean;
}

/**
 * Root post summary shown at the top of the comments screen.
 */
export interface RootPost {
  /** Unique post identity */
  readonly id: string;
  /** Author of the post */
  readonly author: AuthorSummary;
  /** Post text content */
  readonly body: string;
  /** ISO 8601 timestamp when post was created */
  readonly createdAt: string;
}

/**
 * Individual comment on a post.
 */
export interface Comment {
  /** Unique comment identity */
  readonly id: string;
  /** Author of the comment */
  readonly author: AuthorSummary;
  /** Comment text content */
  readonly body: string;
  /** ISO 8601 timestamp when comment was created */
  readonly createdAt: string;
  /** Number of likes on this comment */
  readonly likeCount: number;
  /** Whether the current user has liked this comment */
  readonly likedByCurrentUser: boolean;
}

/**
 * Composer state for writing new comments.
 */
export interface ComposerState {
  /** Current draft text */
  readonly draft: string;
  /** Whether a submission is in flight */
  readonly isSubmitting: boolean;
  /** Whether submission failed (draft should be preserved) */
  readonly hasFailed: boolean;
}

/**
 * Lifecycle status values for the comments screen program.
 */
export type CommentsStatus =
  | 'loading'     // Loading post and comments data
  | 'ready'       // Showing post and comments
  | 'not_found'   // Post ID invalid or not found
  | 'error';      // An error occurred

/**
 * Complete state shape for the Comments program.
 */
export interface CommentsModel {
  /** Current lifecycle status */
  readonly status: CommentsStatus;
  /** Root post being commented on (null when loading/error/not_found) */
  readonly rootPost: RootPost | null;
  /** List of comments (empty when loading/error/not_found) */
  readonly comments: readonly Comment[];
  /** Composer state for new comment submission */
  readonly composer: ComposerState;
  /** Error message when status is 'error' */
  readonly error: string | null;
  /**
   * Deterministic submission counter — generates IDs inside the pure reducer.
   * Starts at 1. Each accepted submission consumes the current value and increments.
   * Non-reusable: once assigned to activeSubmissionId it is consumed.
   */
  readonly nextSubmissionId: number;
  /**
   * Active submission identity.
   * Non-null when a submission is in-flight.
   * Used to ignore stale completions.
   */
  readonly activeSubmissionId: number | null;
  /** Current user author summary for the composer avatar */
  readonly currentUser: AuthorSummary | null;
  /**
   * Active load generation — increments on every LoadStarted.
   * Used to reject stale load completions when route changes.
   */
  readonly activeLoadId: number;
}

// =============================================================================
// Initial state
// =============================================================================

/**
 * Initial/empty state for the comments program.
 */
export const initialModel: CommentsModel = {
  status: 'loading',
  rootPost: null,
  comments: [],
  composer: {
    draft: '',
    isSubmitting: false,
    hasFailed: false,
  },
  error: null,
  nextSubmissionId: 1,
  activeSubmissionId: null,
  currentUser: null,
  activeLoadId: 0,
};

// =============================================================================
// Pure helper functions
// =============================================================================

/**
 * Derive author initials from display name.
 * Takes the first Unicode character that is a letter, uppercase.
 */
export function deriveInitials(displayName: string): string {
  if (!displayName || displayName.trim().length === 0) {
    return '?';
  }
  const first = displayName.trim()[0] ?? '';
  return first.toUpperCase();
}

/**
 * Build AuthorSummary from profile data.
 */
export function buildAuthorSummary(params: {
  displayName: string;
  age: number | null;
  isAnonymous?: boolean;
}): AuthorSummary {
  const displayName = params.isAnonymous ? 'Аноним' : params.displayName;
  return {
    displayName,
    age: params.isAnonymous ? null : params.age,
    initials: deriveInitials(displayName),
    isAnonymous: params.isAnonymous ?? false,
  };
}

/**
 * Check if the composer draft is valid for submission.
 * Requires: non-empty and not whitespace-only.
 */
export function isComposerValid(model: CommentsModel): boolean {
  const { draft, isSubmitting } = model.composer;
  return !isSubmitting && draft.trim().length > 0;
}

/**
 * Check if the send button should be disabled.
 */
export function isSendDisabled(model: CommentsModel): boolean {
  return !isComposerValid(model);
}
